import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { auth, db } from "../../lib/firebase";
import { onAuthStateChanged } from "firebase/auth";
import { deleteDoc, doc, getDoc, serverTimestamp, updateDoc } from "firebase/firestore";

function safe(v) {
  return String(v ?? "").trim();
}
function money(n, cur = "GHS") {
  const num = Number(n || 0);
  if (Number.isNaN(num)) return `${cur} —`;
  return `${cur} ${num.toLocaleString()}`;
}
function fmtDate(ts) {
  try {
    if (!ts) return "—";
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString();
  } catch {
    return "—";
  }
}

export default function SellerViewProductScreen({ goBack }) {
  const { productId: rawProductId } = useParams();
  const productId = useMemo(() => safe(rawProductId), [rawProductId]);

  const [user, setUser] = useState(null);
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUser(u || null));
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!user || !productId) return;

    let alive = true;

    (async () => {
      setLoading(true);
      setMsg("");

      try {
        const snap = await getDoc(doc(db, "products", productId));

        if (!snap.exists()) {
          if (!alive) return;
          setP(null);
          setMsg("Product not found.");
          setLoading(false);
          return;
        }

        const data = snap.data() || {};

        // ✅ seller can only view their own product
        if (safe(data.storeId) !== safe(user.uid)) {
          if (!alive) return;
          setP(null);
          setMsg("You don’t have permission to view this product.");
          setLoading(false);
          return;
        }

        if (!alive) return;
        setP({ id: snap.id, ...data });
        setLoading(false);
      } catch (e) {
        if (!alive) return;
        setMsg(e?.message || "Failed to load product.");
        setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [user, productId]);

  const images = useMemo(() => {
    const a = Array.isArray(p?.mediaUrls) ? p.mediaUrls : [];
    if (a.length) return a;
    const single = safe(p?.thumbnailUrl);
    return single ? [single] : [];
  }, [p]);

  const isActive = useMemo(() => {
    if (!p) return false;
    const status = safe(p?.status);
    if (status) return status === "ACTIVE";
    return p?.isActive !== false;
  }, [p]);

  const goEdit = () => (window.location.href = `/seller/products/${productId}/edit`);
  const previewPublic = () => window.open(`/p/${productId}`, "_blank");

  const toggleActive = async () => {
    if (!user || !p) return;
    setMsg("");

    try {
      const nextStatus = isActive ? "DRAFT" : "ACTIVE";

      await updateDoc(doc(db, "products", p.id), {
        status: nextStatus,
        isActive: nextStatus === "ACTIVE",
        updatedAt: serverTimestamp(),
      });

      setP((s) => ({ ...s, status: nextStatus, isActive: nextStatus === "ACTIVE" }));
      setMsg(isActive ? "✅ Hidden (DRAFT)" : "✅ Now visible (ACTIVE)");
    } catch (e) {
      setMsg(e?.message || "Failed to update.");
    }
  };

  const removeProduct = async () => {
    if (!user || !p) return;
    const ok = window.confirm(`Delete "${safe(p.title) || "product"}"?`);
    if (!ok) return;

    try {
      await deleteDoc(doc(db, "products", p.id));
      setMsg("✅ Deleted.");
      setTimeout(() => goBack?.(), 600);
    } catch (e) {
      setMsg(e?.message || "Failed to delete.");
    }
  };

  return (
    <div className="sv-page">
      <style>{css}</style>

      <div className="sv-wrap">
        <div className="sv-top">
          <button className="sv-back" onClick={goBack}>
            ← Back
          </button>

          <div className="sv-title">Product</div>

          <div className="sv-actions">
            <button className="sv-soft" onClick={toggleActive}>
              {isActive ? "Hide" : "Make Active"}
            </button>
            <button className="sv-soft" onClick={goEdit}>
              Edit
            </button>
            <button className="sv-soft" onClick={previewPublic}>
              Preview
            </button>
            <button className="sv-danger" onClick={removeProduct}>
              Delete
            </button>
          </div>
        </div>

        {msg ? <div className={`sv-msg ${msg.includes("✅") ? "" : "is-err"}`}>{msg}</div> : null}

        {loading ? (
          <div className="sv-card">Loading…</div>
        ) : !p ? (
          <div className="sv-card">No product.</div>
        ) : (
          <div className="sv-grid">
            <div className="sv-card">
              <div className="sv-gallery">
                {images.length ? (
                  images.map((u) => (
                    <div className="sv-img" key={u}>
                      <img src={u} alt="product" />
                    </div>
                  ))
                ) : (
                  <div className="sv-empty">No images</div>
                )}
              </div>
            </div>

            <div className="sv-card">
              <div className="sv-h1">{safe(p.title) || "Untitled"}</div>

              <div className="sv-sub">
                {money(p.price, safe(p.currency) || "GHS")}
                {p?.hasDiscount ? (
                  <span className="sv-badge">Discount {Number(p.discountPercent || 0)}%</span>
                ) : null}
                <span className={`sv-pill ${isActive ? "" : "off"}`}>{isActive ? "ACTIVE" : "DRAFT"}</span>
              </div>

              {safe(p.productType) ? <div className="sv-kv"><b>Type:</b> {safe(p.productType)}</div> : null}
              {safe(p.categoryName || p.categoryId) ? (
                <div className="sv-kv"><b>Category:</b> {safe(p.categoryName) || safe(p.categoryId)}</div>
              ) : null}

              <div className="sv-kv"><b>Stock:</b> {Number(p.stock || 0)}</div>
              <div className="sv-kv"><b>Updated:</b> {fmtDate(p.updatedAt)}</div>

              {safe(p.description) ? (
                <div className="sv-desc">
                  <div className="sv-h2">Description</div>
                  <div className="sv-text">{safe(p.description)}</div>
                </div>
              ) : null}

              {p?.attributes && Object.keys(p.attributes).length ? (
                <div className="sv-desc">
                  <div className="sv-h2">Attributes</div>
                  <div className="sv-attrs">
                    {Object.entries(p.attributes).map(([k, v]) => (
                      <div className="sv-attr" key={k}>
                        <span className="k">{safe(k)}</span>
                        <span className="v">{safe(v)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="sv-meta">
                <div><b>productId:</b> {p.id}</div>
                <div><b>storeId:</b> {safe(p.storeId)}</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const css = `
.sv-page{min-height:100vh;background:#f3f6ff;padding:16px;font-family:Inter,system-ui,Segoe UI,Arial;color:#0f172a;}
.sv-wrap{max-width:1100px;margin:0 auto;display:grid;gap:12px;}
.sv-top{display:flex;align-items:center;justify-content:space-between;gap:10px;flex-wrap:wrap;}
.sv-back{border:1px solid #e7ecf6;background:#fff;border-radius:12px;padding:10px 12px;font-weight:900;cursor:pointer;}
.sv-title{font-weight:1100;font-size:18px;}
.sv-actions{display:flex;gap:8px;flex-wrap:wrap;}
.sv-soft{border:1px solid #e7ecf6;background:#fff;border-radius:12px;padding:10px 12px;font-weight:900;cursor:pointer;}
.sv-danger{border:1px solid #fecdd3;background:#fff1f2;color:#be123c;border-radius:12px;padding:10px 12px;font-weight:900;cursor:pointer;}
.sv-msg{border:1px solid #e7ecf6;background:#fff;border-radius:14px;padding:12px;font-weight:900;}
.sv-msg.is-err{border-color:rgba(255,70,120,0.35);background:rgba(255,70,120,0.06);color:#b91c1c;}
.sv-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;}
.sv-card{background:#fff;border:1px solid #e7ecf6;border-radius:18px;padding:12px;box-shadow:0 10px 26px rgba(15,23,42,0.06);}
.sv-gallery{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px;}
.sv-img{border:1px solid #e7ecf6;border-radius:14px;overflow:hidden;background:#eef2ff;}
.sv-img img{width:100%;height:220px;object-fit:cover;display:block;}
.sv-empty{padding:22px;border:1px dashed #cbd5e1;border-radius:14px;color:#64748b;font-weight:900;}
.sv-h1{font-size:20px;font-weight:1100;}
.sv-sub{margin-top:6px;display:flex;gap:8px;align-items:center;flex-wrap:wrap;color:#0f172a;font-weight:1000;}
.sv-badge{background:#0f172a;color:#fff;border-radius:999px;padding:6px 10px;font-weight:1000;font-size:12px;}
.sv-pill{border:1px solid #e7ecf6;border-radius:999px;padding:6px 10px;font-weight:1000;font-size:12px;background:#f7fbff;color:#0f172a;}
.sv-pill.off{background:#fff1f2;border-color:#fecdd3;color:#be123c;}
.sv-kv{margin-top:10px;color:#0f172a;font-weight:900;}
.sv-desc{margin-top:14px;border-top:1px solid #eef2ff;padding-top:12px;}
.sv-h2{font-weight:1100;margin-bottom:8px;}
.sv-text{white-space:pre-wrap;color:#334155;font-weight:900;line-height:1.45;}
.sv-attrs{display:grid;gap:8px;}
.sv-attr{display:flex;justify-content:space-between;gap:10px;border:1px solid #eef2ff;border-radius:12px;padding:10px;font-weight:900;}
.sv-attr .k{color:#64748b;}
.sv-attr .v{color:#0f172a;}
.sv-meta{margin-top:14px;border-top:1px solid #eef2ff;padding-top:12px;color:#64748b;font-weight:900;font-size:12px;display:grid;gap:6px;}
@media(max-width:900px){.sv-grid{grid-template-columns:1fr;}.sv-img img{height:180px;}}
`;
